import type { Plan, PlanDiscountInfo, PlanSku } from '@/app/actions/cart';
import type { User } from '@/app/actions/user';
import { hasCurrentPeriodUpgradeTarget } from './upgrade-guide-utils';

// ---- 常量 ----

export const VALIDITY_PERIOD_LABEL: Record<string, string> = {
  today: '当天',
  one_day: '1天',
  one_week: '1周',
  one_month: '1个月',
  two_months: '2个月',
  three_months: '3个月',
  six_months: '半年',
  one_year: '1年',
  no_expiry: '永久',
};

/** subscribe-plans 专用的周期标签（月付/季付/年付） */
export const SUBSCRIBE_PERIOD_LABEL: Record<string, string> = {
  one_month: '月付',
  three_months: '季付',
  one_year: '年付',
};

export const MEMBERSHIP_PERIOD_CARD_LABEL: Record<string, string> = {
  one_month: '月卡',
  three_months: '季卡',
  one_year: '年卡',
};

export const PERIOD_MONTHS: Record<string, number> = {
  one_month: 1,
  two_months: 2,
  three_months: 3,
  six_months: 6,
  one_year: 12,
};

export const PERIOD_TABS = ['one_month', 'three_months', 'one_year'] as const;

// ---- 折扣相关 ----

export function getEffectiveDiscount(
  plan: Plan,
  sku: PlanSku,
): PlanDiscountInfo | null {
  return sku.discount_info ?? plan.discount_info ?? null;
}

export function applyDiscount(plan: Plan, sku: PlanSku): number {
  const price = sku.sale_price;
  const discount = getEffectiveDiscount(plan, sku);
  if (!discount) return price;
  const { percentage, fixed_amount } = discount;
  if (percentage != null) {
    return Math.round((price * percentage) / 100);
  }
  if (fixed_amount != null) {
    return Math.max(0, price - fixed_amount);
  }
  return price;
}

export function applyDiscountRaw(
  price: number,
  discount: PlanDiscountInfo | null,
): number {
  if (!discount) return price;
  if (discount.percentage != null) {
    return Math.round((price * discount.percentage) / 100);
  }
  if (discount.fixed_amount != null) {
    return Math.max(0, price - discount.fixed_amount);
  }
  return price;
}

export function hasDiscountForPeriod(plans: Plan[], period: string): boolean {
  return plans.some((plan) => {
    const sku = plan.skus.find((s) => s.validity_period === period);
    return sku != null && getEffectiveDiscount(plan, sku) != null;
  });
}

/** 该周期内最低折扣率（如 7.5 表示七五折），无折扣返回 null */
export function getLowestDiscountRate(
  plans: Plan[],
  period: string,
): number | null {
  let lowest: number | null = null;
  for (const plan of plans) {
    const sku = plan.skus.find((s) => s.validity_period === period);
    if (!sku || sku.sale_price <= 0) continue;
    const discounted = applyDiscount(plan, sku);
    if (discounted >= sku.sale_price) continue;
    const rate = Math.round((discounted / sku.sale_price) * 10 * 10) / 10; // 保留一位小数
    if (lowest === null || rate < lowest) lowest = rate;
  }
  return lowest;
}

// ---- 价格计算 ----

export function getSavePct(plans: Plan[], period: string): number | null {
  const months = PERIOD_MONTHS[period];
  if (!months || months <= 1) return null;
  let maxPct = 0;
  for (const plan of plans) {
    const monthlySku = plan.skus.find(
      (s) => s.validity_period === 'one_month' && s.sale_price > 0,
    );
    const targetSku = plan.skus.find(
      (s) => s.validity_period === period && s.sale_price > 0,
    );
    if (!monthlySku || !targetSku) continue;
    const monthlyTotal = monthlySku.sale_price * months;
    if (monthlyTotal <= 0) continue;
    const pct = Math.round((1 - targetSku.sale_price / monthlyTotal) * 100);
    if (pct > maxPct) maxPct = pct;
  }
  return maxPct > 0 ? maxPct : null;
}

export interface PriceInfo {
  totalPrice: number;
  months: number;
  monthlyPrice: number;
  priceBeforeDiscount: number;
  discountSaved: number | null;
  periodSaved: number | null;
  discount: PlanDiscountInfo | null;
  periodLabel: string;
}

export function computePriceInfo(plan: Plan, sku: PlanSku): PriceInfo {
  const totalPrice = applyDiscount(plan, sku);
  const months = PERIOD_MONTHS[sku.validity_period] ?? 1;
  const monthlyPrice =
    months > 1 ? Math.round(totalPrice / months) : totalPrice;
  const priceBeforeDiscount = sku.sale_price;
  const discount = getEffectiveDiscount(plan, sku);
  const discountSaved =
    discount && priceBeforeDiscount > totalPrice
      ? priceBeforeDiscount - totalPrice
      : null;

  const monthlySku = plan.skus.find((s) => s.validity_period === 'one_month');
  const monthlyFullPrice = monthlySku ? applyDiscount(plan, monthlySku) : null;
  const periodSaved =
    months > 1 && monthlyFullPrice
      ? monthlyFullPrice * months - totalPrice
      : null;

  return {
    totalPrice,
    months,
    monthlyPrice,
    priceBeforeDiscount,
    discountSaved,
    periodSaved,
    discount,
    periodLabel: SUBSCRIBE_PERIOD_LABEL[sku.validity_period] ?? sku.name ?? '',
  };
}

export function getMembershipPeriodCardLabel(period: string): string {
  return (
    MEMBERSHIP_PERIOD_CARD_LABEL[period] ??
    VALIDITY_PERIOD_LABEL[period] ??
    period
  );
}

export function buildFeatures(plan: Plan): string[] {
  const features: string[] = [];
  if (plan.point_amount_per_month != null && plan.point_amount_per_month > 0) {
    features.push(
      `每月 ${plan.point_amount_per_month.toLocaleString()} 积分， 当月有效`,
    );
  }
  if (plan.max_file_size_mb != null) {
    features.push(`文件翻译上传单个文件最大 ${plan.max_file_size_mb}MB`);
  }
  if (plan.max_concurrent_tasks != null) {
    features.push(`最多同时执行 ${plan.max_concurrent_tasks} 个文件翻译任务`);
  }
  if (plan.translation_storage_days != null) {
    features.push(`翻译文件保存 ${plan.translation_storage_days} 天`);
  }
  if (plan.daily_sign_in_points != null && plan.daily_sign_in_points > 0) {
    features.push(`每日登录赠送 ${plan.daily_sign_in_points} 积分`);
  }
  if (plan.priority_support) {
    features.push('优先客服支持');
  }
  if (plan.features) {
    const existing = new Set(features);
    features.push(...plan.features.filter((f: string) => !existing.has(f)));
  }
  return features;
}

// ---- CTA 状态 ----

export interface CtaState {
  label: string;
  disabled: boolean;
  hidden: boolean;
  loading?: boolean;
  action: 'buy' | 'login' | 'none';
}

export function getCtaState(
  plan: Pick<Plan, 'tier_rank' | 'skus'>,
  isFree: boolean,
  selectedSku: Pick<PlanSku, 'validity_period'> | null | undefined,
  user: User | null,
  currentMembershipPeriod?: string | null,
): CtaState {
  if (isFree) {
    const isMember = user?.membership && (user?.pointInfo?.tier_rank ?? 0) > 0;
    if (isMember)
      return { label: '', disabled: true, hidden: true, action: 'none' };
    if (!user)
      return {
        label: '登录使用',
        disabled: false,
        hidden: false,
        action: 'login',
      };
    return {
      label: '当前方案',
      disabled: true,
      hidden: false,
      action: 'none',
    };
  }

  if (!selectedSku) {
    return {
      label: '立即购买',
      disabled: true,
      hidden: false,
      action: 'none',
    };
  }

  const label = hasCurrentPeriodUpgradeTarget(
    plan,
    user,
    currentMembershipPeriod,
  )
    ? '升级方案'
    : '立即购买';

  return {
    label,
    disabled: false,
    hidden: false,
    action: 'buy',
  };
}
